import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Store, Eye, Check, X, Ban, Loader2, FileText, ExternalLink, Trash2 } from "lucide-react";

const TABS = [
  { key: "", label: "All" },
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
  { key: "suspended", label: "Suspended" },
];

const STATUS_STYLES = {
  pending: "bg-amber-100 text-amber-700",
  approved: "bg-sage/15 text-sage",
  rejected: "bg-red-100 text-red-700",
  suspended: "bg-ink/10 text-ink-muted",
};

function DocLink({ label, id }) {
  if (!id) return null;
  return (
    <a href={fileUrl(id)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-ink-soft hover:border-brand" data-testid={`doc-${label}`}>
      <FileText className="h-3.5 w-3.5" /> {label} <ExternalLink className="h-3 w-3" />
    </a>
  );
}

export default function AdminSellers() {
  const [sellers, setSellers] = useState([]);
  const [counts, setCounts] = useState({});
  const [tab, setTab] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: list }, { data: c }] = await Promise.all([
        api.get(`/admin/sellers${tab ? `?status=${tab}` : ""}`),
        api.get("/admin/sellers/counts"),
      ]);
      setSellers(list);
      setCounts(c);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (seller, status) => {
    let reason = "";
    if (status === "rejected" || status === "suspended") {
      reason = window.prompt(`Reason for ${status} (optional):`, "") || "";
    }
    setBusy(true);
    try {
      await api.put(`/admin/sellers/${seller.id}/status`, { status, reason });
      toast.success(`Seller ${status}`);
      setSelected(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const deleteSeller = async (seller) => {
    if (!window.confirm(`Delete "${seller.shop_name}"? This permanently removes the seller along with their products and orders.`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/sellers/${seller.id}`);
      toast.success("Seller deleted");
      setSelected(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-sellers">
      <div>
        <h1 className="font-heading text-2xl font-bold text-ink sm:text-3xl">Sellers</h1>
        <p className="text-ink-soft">Review and approve shops that want to sell on NICEE.</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`seller-tab-${t.label.toLowerCase()}`}
            className={`rounded-full px-4 py-1.5 text-sm font-semibold ${tab === t.key ? "bg-brand text-ink" : "border border-border text-ink-soft hover:border-brand"}`}>
            {t.label} {counts[t.key || "all"] != null && <span className="ml-1 opacity-70">({counts[t.key || "all"]})</span>}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>
      ) : sellers.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-white/60 p-12 text-center">
          <Store className="mx-auto h-10 w-10 text-ink-muted" />
          <p className="mt-3 font-heading text-lg text-ink">No sellers here yet</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-border bg-white">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-border text-xs uppercase tracking-wide text-ink-muted">
              <tr>
                <th className="px-4 py-3">Shop</th>
                <th className="px-4 py-3">Owner</th>
                <th className="px-4 py-3">Mobile</th>
                <th className="px-4 py-3">City</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sellers.map((s) => (
                <tr key={s.id} className="border-b border-border/50 last:border-0" data-testid={`seller-row-${s.id}`}>
                  <td className="px-4 py-3 font-semibold text-ink">{s.shop_name}</td>
                  <td className="px-4 py-3 text-ink-soft">{s.owner_name}</td>
                  <td className="px-4 py-3 text-ink-soft">{s.mobile}</td>
                  <td className="px-4 py-3 text-ink-soft">{s.city}</td>
                  <td className="px-4 py-3 text-ink-soft">{s.business_type}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2.5 py-1 text-xs font-semibold capitalize ${STATUS_STYLES[s.status] || "bg-cream-alt"}`}>{s.status}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1.5">
                      <Button size="sm" variant="outline" className="rounded-full" onClick={() => setSelected(s)} data-testid={`seller-view-${s.id}`}><Eye className="h-4 w-4" /></Button>
                      {s.status !== "approved" && (
                        <Button size="sm" className="rounded-full bg-sage text-white hover:bg-sage/90" onClick={() => changeStatus(s, "approved")} data-testid={`seller-approve-${s.id}`}><Check className="h-4 w-4" /></Button>
                      )}
                      {s.status !== "rejected" && s.status !== "suspended" && (
                        <Button size="sm" variant="outline" className="rounded-full text-destructive" onClick={() => changeStatus(s, "rejected")} data-testid={`seller-reject-${s.id}`}><X className="h-4 w-4" /></Button>
                      )}
                      <Button size="sm" variant="ghost" className="rounded-full text-destructive" onClick={() => deleteSeller(s)} data-testid={`seller-delete-${s.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {selected.logo_id ? <img src={fileUrl(selected.logo_id)} alt="" className="h-8 w-8 rounded-lg object-cover" /> : <Store className="h-5 w-5 text-brand" />}
                  {selected.shop_name}
                  <span className={`ml-2 rounded-full px-2 py-0.5 text-xs font-semibold capitalize ${STATUS_STYLES[selected.status]}`}>{selected.status}</span>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 text-sm">
                <Section title="Owner & Contact">
                  <Row k="Owner" v={selected.owner_name} />
                  <Row k="Mobile" v={selected.mobile} />
                  <Row k="WhatsApp" v={selected.whatsapp_number} />
                  <Row k="Email" v={selected.email} />
                  {selected.alternate_phone && <Row k="Alt Phone" v={selected.alternate_phone} />}
                </Section>
                <Section title="Shop">
                  <Row k="Type" v={selected.business_type} />
                  <Row k="Address" v={[selected.address, selected.area, selected.city, selected.district, selected.state, selected.pincode].filter(Boolean).join(", ")} />
                  {selected.map_link && <Row k="Map" v={<a href={selected.map_link} target="_blank" rel="noopener noreferrer" className="text-brand underline">Open map</a>} />}
                  {(selected.lat || selected.lng) && <Row k="Lat/Lng" v={`${selected.lat}, ${selected.lng}`} />}
                  {selected.categories_sold?.length > 0 && <Row k="Categories" v={selected.categories_sold.join(", ")} />}
                  {(selected.opening_time || selected.closing_time) && <Row k="Timings" v={`${selected.opening_time || "—"} - ${selected.closing_time || "—"}`} />}
                  {selected.working_days?.length > 0 && <Row k="Working days" v={selected.working_days.join(", ")} />}
                </Section>
                <Section title="Business & Fulfillment">
                  {selected.gstin && <Row k="GSTIN" v={selected.gstin} />}
                  {selected.pan && <Row k="PAN" v={selected.pan} />}
                  {selected.fssai && <Row k="FSSAI" v={selected.fssai} />}
                  {selected.business_reg_no && <Row k="Reg. No" v={selected.business_reg_no} />}
                  <Row k="Fulfillment" v={`${selected.seller_delivery ? "Seller delivery" : ""}${selected.seller_delivery && selected.customer_pickup ? " · " : ""}${selected.customer_pickup ? "Customer pickup" : ""}` || "—"} />
                  {selected.delivery_radius && <Row k="Delivery radius" v={`${selected.delivery_radius} km`} />}
                </Section>
                {selected.photo_ids?.length > 0 && (
                  <Section title="Shop Photos">
                    <div className="flex flex-wrap gap-2">
                      {selected.photo_ids.map((id) => <img key={id} src={fileUrl(id)} alt="" className="h-20 w-20 rounded-xl object-cover" />)}
                    </div>
                  </Section>
                )}
                <Section title="Documents">
                  <div className="flex flex-wrap gap-2">
                    <DocLink label="GST" id={selected.doc_gst_id} />
                    <DocLink label="PAN" id={selected.doc_pan_id} />
                    <DocLink label="FSSAI" id={selected.doc_fssai_id} />
                    <DocLink label="License" id={selected.doc_license_id} />
                    {!selected.doc_gst_id && !selected.doc_pan_id && !selected.doc_fssai_id && !selected.doc_license_id && <span className="text-ink-muted">No documents uploaded</span>}
                  </div>
                </Section>
                {selected.rejection_reason && <p className="rounded-lg bg-red-50 p-3 text-red-700">Reason: {selected.rejection_reason}</p>}
              </div>
              <div className="mt-2 flex flex-wrap justify-end gap-2 border-t border-border pt-4">
                {selected.status !== "approved" && (
                  <Button disabled={busy} className="rounded-full bg-sage text-white hover:bg-sage/90" onClick={() => changeStatus(selected, "approved")} data-testid="seller-detail-approve"><Check className="mr-1 h-4 w-4" /> Approve</Button>
                )}
                {selected.status !== "suspended" && selected.status === "approved" && (
                  <Button disabled={busy} variant="outline" className="rounded-full" onClick={() => changeStatus(selected, "suspended")} data-testid="seller-detail-suspend"><Ban className="mr-1 h-4 w-4" /> Suspend</Button>
                )}
                {selected.status !== "rejected" && (
                  <Button disabled={busy} variant="outline" className="rounded-full text-destructive" onClick={() => changeStatus(selected, "rejected")} data-testid="seller-detail-reject"><X className="mr-1 h-4 w-4" /> Reject</Button>
                )}
                <Button disabled={busy} variant="ghost" className="rounded-full text-destructive" onClick={() => deleteSeller(selected)} data-testid="seller-detail-delete"><Trash2 className="mr-1 h-4 w-4" /> Delete</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-ink-muted">{title}</h3>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex gap-2">
      <span className="w-28 shrink-0 text-ink-muted">{k}</span>
      <span className="text-ink">{v || "—"}</span>
    </div>
  );
}
